<template>
    <v-container class="pa-5" fluid>
        <v-dialog
            v-model="toolboxPreviewDialog"
            scrollable
            max-width="600px"
            eager
            @keydown.esc="closeToolboxPreviewDialog"
            @click:outside="closeToolboxPreviewDialog"
        >
            <v-card tile>
                <v-card-text>
                    <img ref="toolboxImagePreview" class="image-preview">
                </v-card-text>
                <v-card-actions>
                    <v-btn data-sentry-label="Zamknij" text color="info" @click="closeToolboxPreviewDialog">
                        Zamknij
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <DeliveryDialog
            :is-open="deliveryDialog"
            :order-id="order.id"
            :delivery-data="currentDelivery"
            :countries="countries"
            @on-save="updateDelivery"
            @on-close="closeDeliveryDialog"
        />

        <ParcelsDialog
            :is-open="parcelsDialog"
            :order-type="'ecommerce'"
            :order-id="order.id"
            :delivery-data="currentDelivery"
            @on-save="refreshOrder"
            @on-close="closeParcelsDialog"
        />

        <SendingsDialog
            :is-open="sendingsDialog"
            :delivery-data="currentDelivery"
            :printers="printers"
            @show-sendings-dialog="showSendingsDialog"
            @on-close="closeSendingsDialog"
        />

        <page-title>
            Zamówienie {{ order.productionNumber }}
            <span :class="orderStatusClass" class="text-uppercase text-body-2">
                {{ orderStatus }}
            </span>
        </page-title>

        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    :disabled="actionLoading.saveOrder"
                    @click="close"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Parametry zamówienia
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-card-text>
                                    <v-container class="pa-0">
                                        <v-row v-if="isEditable">
                                            <v-col cols="12" sm="6" md="3">
                                                <v-text-field
                                                    :value="formatDate(order.createdAt)"
                                                    label="Data zamówienia"
                                                    readonly
                                                    outlined
                                                    dense
                                                />
                                            </v-col>
                                            <v-col cols="12" sm="6" md="3">
                                                <v-datetime-picker
                                                    v-model="plannedSendDate"
                                                    label="Planowana data wysyłki"
                                                    date-format="dd.MM.yyyy"
                                                    :date-picker-props="datetimeProps.dateProps"
                                                    :text-field-props="datetimeProps.textFieldProps"
                                                    :time-picker-props="datetimeProps.timeProps"
                                                    @input="plannedSendDateHandler"
                                                >
                                                    <template slot="dateIcon">
                                                        <v-icon>mdi-calendar</v-icon>
                                                    </template>
                                                    <template slot="timeIcon">
                                                        <v-icon>mdi-clock-outline</v-icon>
                                                    </template>
                                                    <template slot="actions" slot-scope="{parent}">
                                                        <v-btn text color="error" @click.native="parent.clearHandler">
                                                            Wyczyść
                                                        </v-btn>
                                                        <v-btn text color="success" @click="parent.okHandler">
                                                            Zapisz
                                                        </v-btn>
                                                    </template>
                                                </v-datetime-picker>
                                            </v-col>
                                            <v-col cols="12" sm="6" md="3">
                                                <v-datetime-picker
                                                    v-model="plannedDeliveryDate"
                                                    label="Planowana data dostawy"
                                                    date-format="dd.MM.yyyy"
                                                    :date-picker-props="datetimeProps.dateProps"
                                                    :text-field-props="datetimeProps.textFieldProps"
                                                    :time-picker-props="datetimeProps.timeProps"
                                                    @input="plannedDeliveryDateHandler"
                                                >
                                                    <template slot="dateIcon">
                                                        <v-icon>mdi-calendar</v-icon>
                                                    </template>
                                                    <template slot="timeIcon">
                                                        <v-icon>mdi-clock-outline</v-icon>
                                                    </template>
                                                    <template slot="actions" slot-scope="{parent}">
                                                        <v-btn text color="error" @click.native="parent.clearHandler">
                                                            Wyczyść
                                                        </v-btn>
                                                        <v-btn text color="success" @click="parent.okHandler">
                                                            Zapisz
                                                        </v-btn>
                                                    </template>
                                                </v-datetime-picker>
                                            </v-col>
                                            <v-col cols="12" sm="6" md="3">
                                                <v-menu
                                                    v-model="receiveFileDatepicker"
                                                    :close-on-content-click="false"
                                                    transition="scale-transition"
                                                    bottom
                                                    nudge-bottom="40"
                                                    nudge-right="34"
                                                    min-width="290px"
                                                >
                                                    <template #activator="{on}">
                                                        <v-text-field
                                                            :value="formatDate(order.receiveFileDate, false, false)"
                                                            label="Data dostarczenia plików"
                                                            prepend-icon="mdi-calendar"
                                                            readonly
                                                            outlined
                                                            dense
                                                            :clearable="!!order.receiveFileDate"
                                                            v-on="on"
                                                            @click:clear="clearReceiveFileDate"
                                                        />
                                                    </template>
                                                    <v-date-picker
                                                        :value="receiveFileDate"
                                                        no-title
                                                        scrollable
                                                        first-day-of-week="1"
                                                        @input="receiveFileDateHandler"
                                                    />
                                                </v-menu>
                                            </v-col>
                                            <v-col cols="12">
                                                <v-textarea
                                                    v-model="order.description"
                                                    label="Uwagi do zamówienia"
                                                    hide-details
                                                    outlined
                                                />
                                            </v-col>
                                        </v-row>
                                        <v-row v-else>
                                            <v-col cols="12" md="6">
                                                <table class="table table--simple">
                                                    <tbody>
                                                        <tr>
                                                            <th>Data zamówienia:</th>
                                                            <td>{{ formatDate(order.createdAt) }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Planowana data wysyłki:</th>
                                                            <td>{{ formatDate(order.plannedSendDate, false) }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </v-col>
                                            <v-col cols="12" md="6">
                                                <table class="table table--simple">
                                                    <tbody>
                                                        <tr>
                                                            <th>Planowana data dostawy:</th>
                                                            <td>{{ formatDate(order.plannedDeliveryDate, false) }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Data dostarczenia plików:</th>
                                                            <td>{{ formatDate(order.receiveFileDate) }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </v-col>
                                            <v-col v-if="order.description" cols="12">
                                                <table class="table table--simple table--align-left">
                                                    <tbody>
                                                        <tr>
                                                            <th>Uwagi do zamówienia:</th>
                                                            <td width="100%">
                                                                {{ order.description }}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container>
                    <v-row align="center">
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Produkty
                            </h2>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-card-text>
                                    <v-data-table
                                        :headers="headers"
                                        :items="order.orderProducts"
                                        disable-pagination
                                        hide-default-footer
                                        :mobile-breakpoint="0"
                                        item-key="index"
                                    >
                                        <template #item.productName="{item}">
                                            {{ item.name }}
                                        </template>
                                        <template #item.productionFile="{item}">
                                            {{ item.orderProductAttachment.length > 0 ? item.orderProductAttachment[0].file : '-' }}
                                        </template>
                                        <template #item.amount="{item}">
                                            {{ item.amount }} szt.
                                        </template>
                                        <template #item.numberOfVersions="{item}">
                                            {{ item.numberOfVersions }}
                                        </template>
                                    </v-data-table>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Adresy do wysyłki
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-data-table
                                    :headers="orderAddressesHeaders"
                                    :items="order.deliveries"
                                    item-key="id"
                                    hide-default-footer
                                    disable-pagination
                                    :mobile-breakpoint="0"
                                    name="orderAddressesData"
                                >
                                    <template #item="{item}">
                                        <tr>
                                            <td :class="{'v-data-table__label v-data-table__label--danger': !item.postCodeProperlyValidated, 'v-data-table__label v-data-table__label--success': item.postCodeProperlyValidated}">
                                                <v-icon
                                                    v-if="!item.postCodeProperlyValidated"
                                                    color="error"
                                                >
                                                    mdi-exclamation
                                                </v-icon>
                                                {{ item.companyName }}, {{ item.firstName }} {{ item.lastName }}, {{ item.street }}, {{ item.zipCode }} {{ item.city }}, {{ item.country }}
                                                <v-btn
                                                    v-if="canEditDelivery && item.status == 10"
                                                    text
                                                    icon
                                                    small
                                                    data-sentry-label="Edytuj adres"
                                                    color="primary"
                                                    @click="showDeliveryDialog(item)"
                                                >
                                                    <v-icon small>
                                                        mdi-pencil
                                                    </v-icon>
                                                </v-btn>
                                            </td>
                                            <td class="text-center">
                                                {{ item.quantityToRead }} szt.
                                            </td>
                                            <td class="text-center">
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <span v-on="on">{{ item.patterns.length }}</span>
                                                    </template>
                                                    <span>
                                                        <span v-for="(pattern, index) in item.patterns" :key="index">
                                                            Wzór {{ pattern.pattern }} <br>
                                                        </span>
                                                    </span>
                                                </v-tooltip>
                                            </td>
                                            <td class="text-center">
                                                {{ formatWeight(item.weight) }}
                                            </td>
                                            <td>
                                                {{ item.parcels.length }}
                                                <v-btn
                                                    v-if="item.status == 10"
                                                    text
                                                    icon
                                                    small
                                                    data-sentry-label="Edytuj kartony"
                                                    color="primary"
                                                    @click="showParcelsDialog(item)"
                                                >
                                                    <v-icon small>
                                                        mdi-pencil
                                                    </v-icon>
                                                </v-btn>
                                            </td>
                                            <td>
                                                <v-btn
                                                    text
                                                    icon
                                                    small
                                                    data-sentry-label="Wysyłki"
                                                    color="primary"
                                                    :disabled="item.sendingLabels.length < 1"
                                                    @click="showSendingsDialog(item)"
                                                >
                                                    <v-icon small>
                                                        mdi-printer
                                                    </v-icon>
                                                </v-btn>
                                            </td>
                                        </tr>
                                    </template>
                                </v-data-table>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container v-if="hasFilePreviews">
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Podgląd plików
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card class="preview">
                                <v-progress-linear
                                    color="blue"
                                    :indeterminate="actionLoading.toolbox"
                                    height="2"
                                />
                                <v-tabs>
                                    <v-tab
                                        v-for="(template, index) in order.numberOfTemplates"
                                        :key="index"
                                        @click="getToolboxTemplateContent(order.toolBoxUrl, index + 1)"
                                    >
                                        Wzór {{ index + 1 }}
                                    </v-tab>
                                </v-tabs>
                                <v-divider />

                                <v-card-text>
                                    <div v-for="(module, key, index) in toolbox.previews" :key="index">
                                        <h3 class="text-subtitle-1 my-2">
                                            {{ $t('productComponents.' + key) }}
                                        </h3>
                                        <a
                                            v-for="(preview, previewIndex) in module"
                                            :key="previewIndex"
                                            class="thumbnail-preview"
                                            @click="openToolboxPreviewDialog(preview.image)"
                                        >
                                            <img :src="preview.thumb">
                                        </a>
                                    </div>
                                </v-card-text>
                                <v-divider />
                                <v-card-actions>
                                    <v-menu offset-y>
                                        <template #activator="{on}">
                                            <v-btn
                                                color="primary"
                                                text
                                                data-sentry-label="Pobranie plików produkcyjnych"
                                                :disabled="!toolbox.productionFiles"
                                                v-on="on"
                                            >
                                                Pliki produkcyjne
                                                <v-icon right dark>
                                                    mdi-download
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list dense>
                                            <v-list-item
                                                v-for="(productionFile, index) in toolbox.productionFiles"
                                                :key="index"
                                                :href="productionFile.link"
                                            >
                                                <v-list-item-title>
                                                    {{ productionFileName(productionFile) }}
                                                </v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </v-card-actions>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Komentarze do zamówienia
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-list v-if="hasComments" two-line>
                                    <template v-for="(comment, index) in order.comments">
                                        <v-list-item
                                            :key="comment.createdAt"
                                        >
                                            <v-list-item-avatar>
                                                <v-avatar
                                                    size="40px"
                                                    color="blue-grey"
                                                >
                                                    <span class="white--text text-uppercase">
                                                        {{ parseUserInitials(comment.username) }}
                                                    </span>
                                                </v-avatar>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    {{ comment.comment }}
                                                </v-list-item-title>
                                                <v-list-item-subtitle>
                                                    {{ comment.username }} - {{ formatDate(comment.createdAt) }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider :key="index" />
                                    </template>
                                </v-list>
                                <v-card-text v-else>
                                    <v-alert
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Brak komentarzy do zamówienia.
                                    </v-alert>
                                </v-card-text>

                                <v-card-text>
                                    <v-textarea
                                        v-model="newComment"
                                        label="Dodaj komentarz"
                                        :disabled="actionLoading.addComment"
                                        auto-grow
                                        clearable
                                        hide-details
                                    />
                                    <v-row justify="end">
                                        <v-col>
                                            <v-btn
                                                color="primary"
                                                data-sentry-label="Wyślij komentarz"
                                                :disabled="newComment === null || newComment.length <= 0"
                                                :loading="actionLoading.addComment"
                                                @click="addComment"
                                            >
                                                Wyślij
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <v-btn
                                v-if="isEditable"
                                color="success"
                                data-sentry-label="Zapisz"
                                :loading="actionLoading.saveOrder"
                                @click="saveOrder"
                            >
                                Zapisz
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'
import store from '@/store'

import PageTitle from '@/components/common/PageTitle'
import DeliveryDialog from './DeliveryDialog'
import ParcelsDialog from './../Common/ParcelsDialog'
import SendingsDialog from './SendingsDialog'

import attachment from '@/mixins/attachment'
import dateFormatter from '@/mixins/dateFormatter'
import parse from '@/mixins/parse'
import tooltipDelay from '@/mixins/tooltip'
import weightFormatter from '@/mixins/weightFormatter'

import { hasUserPermission } from '@/utils/permissionsHandler'
import { errorHandler } from '@/utils/errorHandler'


export default {
    name: 'Order',
    components: {
        PageTitle, DeliveryDialog, ParcelsDialog, SendingsDialog
    },
    mixins: [
        attachment,
        dateFormatter,
        parse,
        tooltipDelay,
        weightFormatter
    ],
    beforeRouteEnter(to, from, next) {
        Promise.all([
            store.dispatch('orders/getOrder', to.params.id),
            store.dispatch('orders/getPrinters')
        ])
            .then(responses => {
                next(vm => {
                    vm.order = responses[0].data
                    vm.plannedSendDate = responses[0].data.plannedSendDate ? new Date(responses[0].data.plannedSendDate) : null
                    vm.plannedDeliveryDate = responses[0].data.plannedDeliveryDate ? new Date(responses[0].data.plannedDeliveryDate) : null
                    vm.receiveFileDate = responses[0].data.receiveFileDate ? responses[0].data.receiveFileDate.split('T')[0] : null

                    if (vm.hasFilePreviews) {
                        vm.getToolboxTemplateContent(vm.order.toolBoxUrl)
                    }
                    vm.createUploadedUrls(vm.order.orderAttachment)
                    vm.printers = responses[1].data.results
                })
            })
            .catch(error => errorHandler(error))
    },
    data() {
        return {
            order: {
                status: {
                    id: null
                },
                plannedSendDate: null,
                plannedDeliveryDate: null,
                receiveFileDate: null,
                deliveries: []
            },
            toolbox: {
                previews: {},
                productionFiles: []
            },
            plannedSendDate: null,
            plannedDeliveryDate: null,
            receiveFileDate: null,
            receiveFileDatepicker: false,
            datetimeProps: {
                textFieldProps: {
                    outlined: true,
                    dense: true,
                    prependIcon: 'mdi-calendar'
                },
                dateProps: {
                    firstDayOfWeek: 1
                },
                timeProps: {
                    format: '24hr',
                    scrollable: true
                }
            },
            toolboxPreviewDialog: false,
            newComment: '',
            deliveryDialog: false,
            currentDelivery: {},
            countries: [],
            parcelsDialog: false,
            sendingsDialog: false,
            printers: [],
            actionLoading: {
                toolbox: false,
                addComment: false,
                saveOrder: false
            },
            headers: [
                {
                    text: 'Nazwa produktu',
                    value: 'productName',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Pliki produkcyjne',
                    value: 'productionFile',
                    align: 'left',
                    sortable: false
                },
                {
                    text: 'Nakład',
                    value: 'amount',
                    align: 'center',
                    sortable: false,
                    width: 150
                },
                {
                    text: 'Ilość wzorów',
                    value: 'numberOfVersions',
                    align: 'center',
                    sortable: false,
                    width: 150
                }
            ],
            orderAddressesHeaders: [
                {
                    text: 'Adres',
                    align: 'left',
                    sortable: false,
                    value: 'address'
                },
                {
                    text: 'Nakład',
                    align: 'center',
                    sortable: false,
                    value: 'quantity'
                },
                {
                    text: 'Ilość wzorów',
                    align: 'center',
                    sortable: false,
                    value: 'patterns'
                },
                {
                    text: 'Waga przesyłki',
                    align: 'center',
                    sortable: false,
                    value: 'weight'
                },
                {
                    text: 'Ilość kartonów',
                    align: 'left',
                    sortable: false,
                    value: 'parcels'
                },
                {
                    text: 'Wysyłki',
                    align: 'left',
                    sortable: false,
                    value: 'actions'
                }
            ]
        }
    },
    computed: {
        isEditable() {
            return this.order.status.id === 10 // new order
        },
        isCFOrder() {
            return this.order.shopPrefix === 'CF'
        },
        hasFilePreviews() {
            return !!this.order.toolBoxUrl
        },
        hasComments() {
            return this.order.comments ? this.order.comments.length > 0 : false
        },
        canEditDelivery() {
            return hasUserPermission('geolocalizations_read')
        },
        orderStatus() {
            let orderStatus = null

            this.order.overdue ? orderStatus = 'opóźnione' : orderStatus = this.order.status.name

            return orderStatus
        },
        orderStatusClass() {
            let orderStatusClass = ''

            if (this.order.overdue) {
                orderStatusClass = 'warning--text'

                return orderStatusClass
            }

            switch (this.order.status.id) {
                case 10:
                case 15:
                case 90:
                    orderStatusClass = 'info--text'
                    break
                case 50:
                case 100:
                    orderStatusClass = 'success--text'
                    break
                case 110:
                case 120:
                    orderStatusClass = 'error--text'
                    break
                default:
                    break
            }

            return orderStatusClass
        },
        userName() {
            return this.$store.state.user.userName
        }
    },
    methods: {
        getOrder() {
            this.$store.dispatch('orders/getOrder', this.order.id)
                .then(response => {
                    this.order = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        refreshOrder(payload) {
            this.order = payload
        },
        plannedSendDateHandler(date) {
            this.plannedSendDate = date
            date ? this.order.plannedSendDate = date.toISOString() : this.order.plannedSendDate = null
        },
        plannedDeliveryDateHandler(date) {
            this.plannedDeliveryDate = date
            date ? this.order.plannedDeliveryDate = date.toISOString() : this.order.plannedDeliveryDate = null
        },
        receiveFileDateHandler(date) {
            this.receiveFileDate = date
            this.order.receiveFileDate = `${date}T00:00:00Z`
            this.receiveFileDatepicker = false
        },
        clearReceiveFileDate() {
            this.receiveFileDate = null,
            this.order.receiveFileDate = null
        },
        addComment() {
            this.actionLoading.addComment = true

            this.$store.dispatch('orders/addComment', {
                comment: this.newComment,
                username: this.userName,
                orderId: this.order.id
            })
                .then(response => {
                    this.order.comments.push(response.data)
                    this.newComment = ''
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.addComment = false
                })
        },
        saveOrder() {
            this.actionLoading.saveOrder = true
            this.$store.dispatch('orders/updateOrder', {
                id: this.order.id,
                plannedSendDate: this.order.plannedSendDate,
                plannedDeliveryDate: this.order.plannedDeliveryDate,
                receiveFileDate: this.order.receiveFileDate,
                description: this.order.description
            })
                .then(() => {
                    router.push({ name: 'ordersEcommerceOrders' })
                        .catch(error => {
                            errorHandler(error)
                        })
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.saveOrder = false
                })
        },
        fileName(file) {
            const words = file.split('/')

            return words[words.length - 1]
        },
        updateDelivery(delivery) {
            const index = this.order.deliveries.findIndex(item => item.id === delivery.id)

            this.order.deliveries.splice(index, 1, delivery)
            this.closeDeliveryDialog()
        },
        showDeliveryDialog(delivery) {
            this.getCountries()
                .then(() => {
                    this.currentDelivery = delivery
                    this.deliveryDialog = true
                })
        },
        closeDeliveryDialog() {
            this.deliveryDialog = false
            setTimeout(() => {
                this.currentDelivery = {}
            }, 500)
        },
        async getCountries() {
            return await this.$store.dispatch('orders/getCountries')
                .then(response => {
                    this.countries = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showSendingsDialog(item) {
            this.sendingsDialog = true
            this.currentDelivery = item
        },
        closeSendingsDialog() {
            this.sendingsDialog = false
            setTimeout(() => {
                this.currentDelivery = {}
            }, 500)
        },
        showParcelsDialog(delivery) {
            this.parcelsDialog = true
            this.currentDelivery = delivery
        },
        closeParcelsDialog() {
            this.parcelsDialog = false
            setTimeout(() => {
                this.currentDelivery = {}
            }, 500)
        },
        getToolboxTemplateContent(toolboxURI, templateId = 1) {
            this.actionLoading.toolbox = true
            this.$store.dispatch('orders/getToolboxTemplateContent', {toolboxURI, templateId})
                .then(response => {
                    this.toolbox = response.data.pattern[templateId]
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.toolbox = false
                })
        },
        openToolboxPreviewDialog(imageURI) {
            const image = this.$refs.toolboxImagePreview

            this.actionLoading.toolbox = true

            image.setAttribute('src', imageURI)
            image.onload = () => {
                this.toolboxPreviewDialog = true
                this.actionLoading.toolbox = false
            }
            image.onerror = () => {
                this.actionLoading.toolbox = false
            }
        },
        closeToolboxPreviewDialog() {
            this.toolboxPreviewDialog = false
        },
        productionFileName(file) {
            const words = file.link.split('/')

            return words[words.length-1]
        },
        close() {
            router.push({ name: 'ordersEcommerceOrders' })
                .catch(error => {
                    errorHandler(error)
                })
        },
    }
}
</script>
